import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import PolicyLink from "@/components/PolicyLink";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EmergencyNotice } from "@/components/incidents/EmergencyNotice";
import api, { formatApiError } from "@/lib/api";
import { DISCLOSURE_GUIDANCE, SAFEGUARDING_CATEGORIES } from "@/config/incidentCatalog";

export default function SafeguardingNewPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [children, setChildren] = useState([]);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [form, setForm] = useState({
    child_id: params.get("child") || "", related_incident_id: params.get("incident") || null,
    identified_date: new Date().toISOString().slice(0, 10), identified_time: "", location: "",
    category: "", factual_description: "", child_said: "", observed: "", immediate_action: "",
    child_safe: "", immediate_danger: false, manager_notified: false, emergency_services: false, other_info: "",
  });
  const [disclosure, setDisclosure] = useState({ datetime: "", context: "", account: "", questions_asked: "", immediate_response: "", who_informed: "" });
  const [showDisclosure, setShowDisclosure] = useState(false);

  useEffect(() => {
    api.get("/children").then((r) => setChildren(r.data.items || [])).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setD = (k, v) => setDisclosure((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.child_id) {
      toast.error("Select the child this concern relates to");
      return;
    }
    setSaving(true);
    setMissing([]);
    try {
      const payload = { ...form, disclosure: showDisclosure ? disclosure : null };
      const { data } = await api.post("/safeguarding", payload);
      toast.success(`Safeguarding concern ${data.sg_number} submitted — authorised staff alerted`);
      navigate("/safeguarding");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) setMissing(detail.missing);
      toast.error(typeof detail === "object" ? detail.message : formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl pb-24" data-testid="sg-new-page">
      <Link to="/safeguarding" data-testid="sg-back-link" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to safeguarding
      </Link>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
        <Lock className="h-6 w-6 text-red-600" /> Raise safeguarding concern
      </h1>
      <PolicyLink module="safeguarding" testid="policy-link-safeguarding" />
      <EmergencyNotice />
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-testid="sg-guidance">
        {DISCLOSURE_GUIDANCE} Categories record a <b>concern / allegation / report</b> — they are not findings.
      </div>

      {missing.length > 0 && (
        <div data-testid="sg-completeness-errors" role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          <p className="font-semibold">Concern is incomplete — missing:</p>
          <ul className="list-disc pl-5">{missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Concern details</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Child / case *</Label>
            <Select value={form.child_id} onValueChange={(v) => set("child_id", v)}>
              <SelectTrigger data-testid="sg-child" className="h-12"><SelectValue placeholder="Select child" /></SelectTrigger>
              <SelectContent className="bg-white">
                {children.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.surname} · {c.case_id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date identified *</Label><Input data-testid="sg-date" type="date" className="h-12" value={form.identified_date} onChange={(e) => set("identified_date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Time</Label><Input data-testid="sg-time" type="time" className="h-12" value={form.identified_time} onChange={(e) => set("identified_time", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Location</Label><Input data-testid="sg-location" className="h-12" value={form.location} onChange={(e) => set("location", e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>Concern category *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="sg-category" className="h-12"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent className="bg-white">
                {SAFEGUARDING_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Factual description *</Label>
            <Textarea data-testid="sg-description" rows={3} value={form.factual_description} onChange={(e) => set("factual_description", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>What the child said (their words where possible)</Label>
            <Textarea data-testid="sg-child-said" rows={2} value={form.child_said} onChange={(e) => set("child_said", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>What was observed</Label>
            <Textarea data-testid="sg-observed" rows={2} value={form.observed} onChange={(e) => set("observed", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Immediate action taken</Label>
            <Textarea data-testid="sg-action" rows={2} value={form.immediate_action} onChange={(e) => set("immediate_action", e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>Is the child currently safe? *</Label>
            <div className="grid grid-cols-3 gap-2">
              {[["yes", "Yes"], ["no", "No"], ["unknown", "Unknown"]].map(([v, label]) => (
                <button key={v} type="button" data-testid={`sg-safe-${v}`} onClick={() => set("child_safe", v)}
                  className={`h-12 rounded-md border text-sm font-semibold transition-colors duration-150 ${form.child_safe === v ? (v === "yes" ? "bg-ukgreen text-white border-ukgreen" : "bg-red-600 text-white border-red-600") : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {[["immediate_danger", "Immediate danger identified?"], ["manager_notified", "Manager / safeguarding lead already notified?"], ["emergency_services", "Emergency services contacted?"]].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <span className="text-sm font-medium text-slate-700">{label}</span>
                <div className="flex gap-1">
                  {[true, false].map((v) => (
                    <button key={String(v)} type="button" data-testid={`sg-${k}-${v}`} onClick={() => set(k, v)}
                      className={`h-10 px-5 rounded-md text-sm font-semibold ${form[k] === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
                      {v ? "Yes" : "No"}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-1.5"><Label>Other relevant information</Label>
            <Textarea data-testid="sg-other" rows={2} value={form.other_info} onChange={(e) => set("other_info", e.target.value)} /></div>
        </CardContent>
      </Card>

      <button type="button" data-testid="sg-disclosure-toggle" onClick={() => setShowDisclosure((s) => !s)}
        className="text-sm text-primary font-medium hover:underline">
        {showDisclosure ? "Hide disclosure section" : "Record a child's disclosure / account"}
      </button>

      {showDisclosure && (
        <Card data-testid="sg-disclosure">
          <CardHeader><CardTitle className="text-base">Child's disclosure / account</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">{DISCLOSURE_GUIDANCE}</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date</Label><Input data-testid="sg-disc-date" type="date" className="h-12" value={disclosure.datetime} onChange={(e) => setD("datetime", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Context</Label><Input data-testid="sg-disc-context" className="h-12" value={disclosure.context} onChange={(e) => setD("context", e.target.value)} /></div>
            </div>
            <div className="space-y-1.5"><Label>Child's account (their words)</Label><Textarea data-testid="sg-disc-account" rows={3} value={disclosure.account} onChange={(e) => setD("account", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Questions asked (open only, never leading)</Label><Textarea data-testid="sg-disc-questions" rows={2} value={disclosure.questions_asked} onChange={(e) => setD("questions_asked", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Immediate response</Label><Textarea data-testid="sg-disc-response" rows={2} value={disclosure.immediate_response} onChange={(e) => setD("immediate_response", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Who was informed</Label><Input data-testid="sg-disc-informed" className="h-12" value={disclosure.who_informed} onChange={(e) => setD("who_informed", e.target.value)} /></div>
          </CardContent>
        </Card>
      )}

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-white/95 backdrop-blur px-4 py-3">
        <div className="mx-auto max-w-2xl">
          <Button className="h-12 w-full bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="sg-submit" disabled={saving} onClick={submit}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit safeguarding concern"}
          </Button>
        </div>
      </div>
    </div>
  );
}
